import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ArrowLeft, ArrowRight, Plus, Minus, Check, Undo2, MessageSquareQuote } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import { Version, VersionChange, useVersionCompare } from './hooks/useVersionHistory';
import { cn } from '../../../lib/utils';
import { summaryValue } from './richTextSummary';

interface VersionDetailProps {
  version: Version;
  onBack: () => void;
}

/** Fields that are internal/noisy and should be hidden from users. */
const NOISY_FIELDS = new Set([
  'id',
  'path',
  'depth',
  'position',
  'board_id',
  'tenant_id',
  'workspace_id',
  'recurrence_parent_id',
  'recurrence_index',
  'parent_task_id',
  'source_module',
  'source_entity_type',
  'source_entity_id',
  'created_from_template_id',
  'schema_version',
  'assignee_names',
  'label_names',
]);

/** Format a date string for display. */
function formatDateValue(value: string): string {
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return format(date, 'MMM d, yyyy');
    }
    return format(date, 'PPp');
  } catch {
    return value;
  }
}

export function VersionDetail({ version, onBack }: VersionDetailProps) {
  const { t } = useTranslation('common');

  const isUpdate = version.change_type === 'updated';
  const isCreate = version.change_type === 'created';
  const hasRichChanges = version.changes && version.changes.length > 0;

  // Only fetch diff from API if no pre-computed changes are available
  const { data: diffData, isLoading: diffLoading } = useVersionCompare(
    version.object_type,
    version.object_id,
    version.version_number - 1,
    version.version_number,
    { enabled: isUpdate && !hasRichChanges && version.version_number > 1 }
  );

  const userName = version.changed_by_name || t('versioning.unknownUser');
  const formattedDate = format(new Date(version.changed_at), 'PPp');

  return (
    <div className="space-y-4">
      {/* Back button */}
      <Button variant="ghost" size="sm" onClick={onBack} className="px-0">
        <ArrowLeft className="h-4 w-4 mr-2 rtl:rotate-180" />
        {t('versioning.backToTimeline')}
      </Button>

      {/* Header */}
      <div className="pb-3 border-b">
        <div className="text-sm">
          <span className="font-medium">{userName}</span>{' '}
          <span className="text-muted-foreground">
            {/* `!= null`: a version replayed from the day-long offline cache can
                predate this field entirely, and `undefined` must read as "no
                revision" rather than render "Revision undefined". */}
            {version.revision_number != null
              ? t('versioning.revision', { number: version.revision_number })
              : t(`versioning.${version.change_type}`).toLowerCase()}
          </span>
        </div>
        <div className="text-xs text-muted-foreground mt-1">{formattedDate}</div>
        {version.change_reason && (
          <div className="mt-2 flex items-start gap-1.5 rounded-md border-s-2 border-primary bg-primary/5 px-2.5 py-1.5">
            <MessageSquareQuote className="h-3.5 w-3.5 shrink-0 mt-0.5 text-primary" />
            <div className="text-xs text-foreground break-words">
              <span className="font-medium">{t('versioning.reason')}:</span>{' '}
              {version.change_reason}
            </div>
          </div>
        )}
      </div>

      {/* Update: show pre-computed changes or fall back to diff API */}
      {isUpdate && (
        hasRichChanges ? (
          <div className="space-y-2">
            {version.changes!.map((change, i) => (
              <ChangeCard key={i} change={change} t={t} />
            ))}
          </div>
        ) : diffLoading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-14 w-full rounded-md" />
            ))}
          </div>
        ) : diffData && Object.keys(diffData.differences).length > 0 ? (
          <div className="space-y-2">
            {Object.entries(diffData.differences)
              .filter(([key]) => !NOISY_FIELDS.has(key))
              .map(([key, diff]) => (
                <DiffField
                  key={key}
                  fieldKey={key}
                  oldValue={diff.old}
                  newValue={diff.new}
                  t={t}
                />
              ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {t('versioning.noDifferences')}
          </p>
        )
      )}

      {/* Created: show meaningful initial values */}
      {isCreate && (
        <div className="space-y-2">
          {Object.entries(version.snapshot)
            .filter(
              ([key, value]) =>
                !NOISY_FIELDS.has(key) &&
                value !== null &&
                value !== undefined &&
                value !== ''
            )
            .map(([key, value]) => (
              <ValueField key={key} fieldKey={key} value={value} t={t} />
            ))}
        </div>
      )}

      {/* Deleted: nothing extra needed, header already says who/when */}
      {version.change_type === 'deleted' && (
        <p className="text-sm text-muted-foreground">
          {t('versioning.deleted')}
        </p>
      )}
    </div>
  );
}

/** Render a single pre-computed change entry as a card. */
function ChangeCard({ change, t }: { change: VersionChange; t: (key: string, opts?: Record<string, unknown>) => string }) {
  const isDateType = change.column_type === 'date' || change.column_type === 'datetime';

  if (change.type === 'list') {
    return (
      <div className="rounded-md border px-3 py-2">
        <div className="text-xs font-medium text-muted-foreground mb-1">
          {change.field}
        </div>
        <div className="space-y-1">
          {change.added?.map((name, i) => (
            <div key={`add-${i}`} className="flex items-center gap-1.5 text-sm text-success">
              <Plus className="h-3 w-3 shrink-0" />
              <span>{name}</span>
            </div>
          ))}
          {change.removed?.map((name, i) => (
            <div key={`rm-${i}`} className="flex items-center gap-1.5 text-sm text-destructive">
              <Minus className="h-3 w-3 shrink-0" />
              <span>{name}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (change.type === 'checklist') {
    const isCompleted = change.action === 'completed';
    const isUncompleted = change.action === 'uncompleted';
    const isAdded = change.action === 'added';

    return (
      <div className="rounded-md border px-3 py-2">
        <div className="text-xs font-medium text-muted-foreground mb-1">
          {change.field}
        </div>
        <div className="flex items-center gap-1.5 text-sm">
          {isCompleted && <Check className="h-3 w-3 text-success shrink-0" />}
          {isUncompleted && <Undo2 className="h-3 w-3 text-warning shrink-0" />}
          {isAdded && <Plus className="h-3 w-3 text-success shrink-0" />}
          {!isCompleted && !isUncompleted && !isAdded && <Minus className="h-3 w-3 text-destructive shrink-0" />}
          <span>
            {change.action === 'completed' && `${t('versioning.changes.completedAction')} `}
            {change.action === 'uncompleted' && `${t('versioning.changes.uncompletedAction')} `}
            {change.action === 'added' && `${t('versioning.changes.addedAction')} `}
            {change.action === 'removed' && `${t('versioning.changes.removedAction')} `}
            {t('versioning.changes.quotedValue', { value: change.new_value || change.old_value })}
          </span>
        </div>
      </div>
    );
  }

  // Field or column_value change
  const oldDisplay = isDateType && change.old_value
    ? formatDateValue(change.old_value)
    : summaryValue(change.old_value);
  const newDisplay = isDateType && change.new_value
    ? formatDateValue(change.new_value)
    : summaryValue(change.new_value);

  return (
    <div className="rounded-md border px-3 py-2">
      <div className="text-xs font-medium text-muted-foreground mb-1">
        {change.field}
      </div>
      <div className="flex items-center gap-2 text-sm">
        <span className={cn(
          'break-words min-w-0',
          oldDisplay
            ? 'text-muted-foreground line-through'
            : 'text-muted-foreground/50 italic'
        )}>
          {oldDisplay || '\u2014'}
        </span>
        <ArrowRight className="h-3 w-3 text-muted-foreground/50 shrink-0" />
        <span className="font-medium break-words min-w-0">
          {newDisplay || '\u2014'}
        </span>
      </div>
    </div>
  );
}

/** A single changed field showing old → new values (legacy diff API fallback). */
function DiffField({
  fieldKey,
  oldValue,
  newValue,
  t,
}: {
  fieldKey: string;
  oldValue: unknown;
  newValue: unknown;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  return (
    <div className="rounded-md border px-3 py-2">
      <div className="text-xs font-medium text-muted-foreground mb-1">
        {formatFieldName(fieldKey)}
      </div>
      <div className="flex items-center gap-2 text-sm">
        <span className={cn(
          'break-words min-w-0',
          oldValue !== null && oldValue !== undefined
            ? 'text-muted-foreground line-through'
            : 'text-muted-foreground/50 italic'
        )}>
          {formatValue(oldValue, t)}
        </span>
        <ArrowRight className="h-3 w-3 text-muted-foreground/50 shrink-0" />
        <span className="font-medium break-words min-w-0">
          {formatValue(newValue, t)}
        </span>
      </div>
    </div>
  );
}

/** A single field with its value (used for created view). */
function ValueField({
  fieldKey,
  value,
  t,
}: {
  fieldKey: string;
  value: unknown;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  return (
    <div className="rounded-md border px-3 py-2">
      <div className="text-xs font-medium text-muted-foreground mb-0.5">
        {formatFieldName(fieldKey)}
      </div>
      <div className="text-sm">{formatValue(value, t)}</div>
    </div>
  );
}

function formatFieldName(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\bid\b/gi, 'ID')
    .replace(/\bids\b/gi, 'IDs')
    .replace(/^./, (s) => s.toUpperCase());
}

function formatValue(value: unknown, t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (value === null || value === undefined) return '\u2014';
  if (typeof value === 'boolean') return value ? t('common:app.yes') : t('common:app.no');
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'string') {
    if (value === '') return '\u2014';
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      try {
        return format(new Date(value), 'PPp');
      } catch {
        return value;
      }
    }
    // A rich-text column arrives here as its stored HTML, and this value is
    // rendered as a TEXT node — so without this it reads back as the literal
    // tags: `Changed Completion notes from <p dir="auto">…</p><ul><li>…`. Every
    // entity's version history has always had it (description and acceptance
    // criteria are rich text too); it became the routine case for a work item
    // when the ship transition started writing `completion_notes` on every ship.
    // Flattened to text rather than rendered as markup on purpose: a diff row is
    // a one-line summary, and a version trail is exactly the surface that must
    // not re-render whatever markup was stored on the way past.
    // `<p></p>` — what a Tiptap editor a user focused and left behind emits —
    // renders nothing, so it means the same as the empty string above.
    return summaryValue(value) || '\u2014';
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return t('common:app.empty');
    if (value.every((v) => typeof v === 'string' || typeof v === 'number')) {
      return value.join(', ');
    }
    return t('versioning.changes.nItems', { count: value.length });
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return t('common:app.empty');
    return t('versioning.changes.nFields', { count: entries.length });
  }
  return String(value);
}
